import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTenantBranding } from '../context/TenantBrandingContext';
import './TenantBranding.css';

/**
 * Wiederverwendbare Komponente für Tenant-Branding
 * Zeigt tenantspezifisches Logo und Namen an
 *
 * ACCESSIBILITY FIXES:
 * - aria-live Region für dynamische Statusänderungen
 * - Screenreader-Ankündigungen für Laden und Fehler
 * - h1 für auth-Variante (korrekte Heading-Hierarchie)
 * - Fokus-Indikatoren mit ausreichendem Kontrast
 *
 * @param {Object} props
 * @param {string} props.variant - "navbar" (kompakt) oder "auth" (größer für Login/Register)
 * @param {string} [props.id] - HTML-ID für Accessibility und E2E-Tests
 * @param {string} [props.className] - Zusätzliche CSS-Klassen
 */
const TenantBranding = ({ variant = 'navbar', id, className = '' }) => {
  const { name, logoUrl, loading, error, navbar } = useTenantBranding();
  const [imageError, setImageError] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const previousLoadingRef = useRef(loading);

  // Screenreader-Ankündigungen für Statusänderungen
  useEffect(() => {
    let timer;

    if (previousLoadingRef.current && !loading) {
      if (error) {
        setStatusMessage('Branding konnte nicht geladen werden. Standardlogo wird verwendet.');
      } else {
        setStatusMessage(`${name} wurde geladen.`);
      }
      timer = setTimeout(() => setStatusMessage(''), 3000);
    }

    previousLoadingRef.current = loading;

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [loading, error, name]);

  // Handler für Bild-Ladefehler mit Screenreader-Ankündigung
  const handleImageError = useCallback(() => {
    setImageError(true);
    setStatusMessage('Logo konnte nicht geladen werden. Standardsymbol wird angezeigt.');
    const timer = setTimeout(() => setStatusMessage(''), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Lade-Zustand anzeigen
  if (loading) {
    return (
      <div
        className={`tenant-branding tenant-branding-${variant} tenant-branding-loading ${className}`}
        id={id}
        role="status"
        aria-busy="true"
        aria-label="Lade Branding-Informationen"
      >
        <div className="spinner-border spinner-border-sm me-2" aria-hidden="true">
          <span className="visually-hidden">Lädt...</span>
        </div>
        <span className="tenant-branding-name">&nbsp;</span>
        {/* Screenreader-Live-Region */}
        <span className="visually-hidden" aria-live="polite">
          Branding wird geladen...
        </span>
      </div>
    );
  }

  // Bestimme ob Logo-Bild oder Fallback-Icon angezeigt werden soll
  const showImage = logoUrl && !imageError;

  // Dynamisches Heading-Element basierend auf Variante
  // auth = h1 (Hauptüberschrift der Seite)
  // navbar = span (keine Heading-Semantik)
  const NameElement = variant === 'auth' ? 'h1' : 'span';

  return (
    <div
      className={`tenant-branding tenant-branding-${variant} ${className}`}
      id={id}
    >
      {showImage ? (
        <img
          src={logoUrl}
          alt={`${name} Logo`}
          className="tenant-branding-logo"
          onError={handleImageError}
          loading="lazy"
        />
      ) : (
        <i
          className="bi bi-file-text tenant-branding-icon"
          aria-hidden="true"
        />
      )}

      {(navbar?.showAppName !== false) && (
        <NameElement className="tenant-branding-name">{name}</NameElement>
      )}

      {/* Screenreader-Live-Region für dynamische Statusmeldungen */}
      <span
        className="visually-hidden"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {statusMessage}
      </span>
    </div>
  );
};

TenantBranding.propTypes = {
  /** Anzeige-Variante: "navbar" (kompakt) oder "auth" (größer für Login/Register) */
  variant: PropTypes.oneOf(['navbar', 'auth']),
  /** HTML-ID für Accessibility und E2E-Tests */
  id: PropTypes.string,
  /** Zusätzliche CSS-Klassen */
  className: PropTypes.string
};

TenantBranding.defaultProps = {
  variant: 'navbar',
  id: undefined,
  className: ''
};

export default TenantBranding;
